<template>
  <Drawer
    :is-open="isOpen"
    :show-x-button="false"
    :large="true"
    :on-click-outside="onClickOutsideHandler"
    :title-icon="true"
    :short="true"
    :use-toast="false"
    class="z-50"
    :title="`${$utils.capitalize(selectedNode?.data?.values?.sub_item?.replaceAll('_from_template', '').replaceAll('_', ' '))} ${$utils
      .capitalize(selectedNode?.data?.type)
      .replaceAll('_', ' ')}`"
    :default-success-msg="
      `${$utils.capitalize(selectedNode?.data?.values?.sub_item?.replaceAll('_', ' '))} ${$utils
        .capitalize(selectedNode?.data?.type)
        .replaceAll('_', ' ')} ` + $t('Saved')
    ">
    <template #form>
      <div
        :class="[
          { 'bg-[#5CE0A9]': selectedNode?.data.type == 'trigger' },
          { 'bg-[#B171F9]': selectedNode?.data.type == 'action' },
          { 'bg-[#59BEE5]': selectedNode?.data.type == 'wait' },
          {
            'bg-[#F97171]':
              selectedNode?.data.type == 'end_branch' ||
              selectedNode?.data.type == 'end_parallel' ||
              selectedNode?.data.type == 'end_for_loop'
          }
        ]"
        class="absolute w-5 h-5 rounded-full -top-12" />

      <div class="relative flex flex-col h-full">
        <div class="flex-1 overflow-y-auto">
          <div v-if="selectedNode?.data.type == 'trigger'" class="overflow-x-hidden overflow-auto h-[80vh] pb-6 pr-2">
            <TriggerForm
              :previous-labels="previous_results"
              :all-previous-labels="all_previous_results"
              :all-labels="all_labels"
              :selected-node="selectedNode"
              class="pb-44 mt-10 h-[80%]"
              @update-values="handleUpdateValues" />
          </div>
          <div v-if="selectedNode?.data.type == 'action'" class="overflow-x-hidden overflow-auto h-[80vh] pb-6 pr-2">
            <div class="mb-10">
              <TextInput
                :value="action_name"
                name="action_name"
                type="text"
                :validate-on-input="true"
                label=""
                :info-message="$t('Action name must be unique, they can be referenced in the following actions.')"
                :placeholder="$t('Action Name')"
                :show-label="true"
                @update:modelValue="
                  (e) => {
                    action_name = e
                    handleUpdateActionName(e)
                  }
                " />
              <div v-if="all_labels.find((l) => l == action_name)" class="text-red-500">
                {{ $t("Action name already exists") }}
              </div>
            </div>

            <div
              v-if="
                selectedNode?.data.values?.sub_item == 'create_plan_from_template' || selectedNode?.data.values?.sub_item == 'create_plan'
              ">
              <EditPlan
                v-if="plan"
                :in-playbook="true"
                :plan="plan"
                :only-show-form="true"
                :all-labels="all_labels"
                :result-references-list="result_refs_calc"
                :result-references-list-for-text="result_refs_name_calc"
                :autocomplete-options-in-playbook="autocomplete_list_calc"
                class="pb-16 mt-10"
                @cancel="onClickOutsideHandler"
                @done="($event) => handlePlanSubmit($event)"
                @update-values="handleUpdateValues" />
              <CreatePlan
                v-else
                :in-playbook="true"
                playbook-object-type="company"
                :playbook-options="autocomplete_list_calc"
                :all-labels="all_labels"
                :previous-labels="previous_results"
                :result-references-list="result_refs_calc"
                :result-references-list-for-text="result_refs_name_calc"
                :autocomplete-options-in-playbook="autocomplete_list_calc"
                class="pb-16 mt-10"
                @cancel="onClickOutsideHandler"
                @done="($event) => handlePlanSubmit($event)" />
            </div>

            <div
              v-if="
                selectedNode?.data.values?.sub_item == 'create_goal_from_template' || selectedNode?.data.values?.sub_item == 'create_goal'
              ">
              <UpdateGoalForm
                v-if="goal"
                :in-playbook="true"
                :goal="goal"
                :only-show-form="true"
                :result-references-list="result_refs_calc"
                :result-references-list-for-text="result_refs_name_calc"
                :autocomplete-options-in-playbook="autocomplete_list_calc"
                :all-labels="all_labels"
                :previous-labels="previous_results"
                class="pb-16 mt-6"
                @cancel="onClickOutsideHandler"
                @done="($event) => handleGoalSubmit($event)"
                @update-values="handleUpdateValues" />

              <transition v-else name="fade" mode="out-in" duration="300">
                <NewGoalForm
                  v-if="show_create_form"
                  :in-playbook="true"
                  :all-labels="all_labels"
                  :previous-labels="previous_results"
                  :result-references-list="result_refs_calc"
                  :result-references-list-for-text="result_refs_name_calc"
                  :autocomplete-options-in-playbook="autocomplete_list_calc"
                  class="pb-16 mt-0"
                  @cancel="onClickOutsideHandler"
                  @done="($event) => handleGoalSubmit($event)" />
              </transition>
            </div>

            <div v-if="selectedNode?.data.values?.sub_item == 'create_task'">
              <UpdateTaskForm
                v-if="task"
                :in-playbook="true"
                :task="task"
                :only-show-form="true"
                :previous-labels="previous_results"
                :result-references-list="result_refs_calc"
                :result-references-list-for-text="result_refs_name_calc"
                :autocomplete-options-in-playbook="autocomplete_list_calc"
                :all-labels="all_labels"
                class="pb-16 mt-6"
                @cancel="onClickOutsideHandler"
                @done="($event) => handleTaskSubmit($event)"
                @update-values="handleUpdateValues" />
              <transition v-else name="fade" mode="out-in" duration="300">
                <CreateNewTask
                  v-if="show_create_form"
                  :in-playbook="true"
                  :previous-labels="previous_results"
                  :all-labels="all_labels"
                  :result-references-list="result_refs_calc"
                  :result-references-list-for-text="result_refs_name_calc"
                  :autocomplete-options-in-playbook="autocomplete_list_calc"
                  class="pb-16 mt-0"
                  @cancel="onClickOutsideHandler"
                  @done="($event) => handleTaskSubmit($event)" />
              </transition>
            </div>

            <div v-if="selectedNode?.data.values?.sub_item == 'create_interaction'">
              <UpdateInteractionForm
                v-if="interaction"
                :in-playbook="true"
                :interaction="interaction"
                :previous-labels="previous_results"
                :all-labels="all_labels"
                :only-show-form="true"
                :result-references-list="result_refs_calc"
                :result-references-list-for-text="result_refs_name_calc"
                :autocomplete-options-in-playbook="autocomplete_list_calc"
                class="mt-0 pb-44 h-[80%]"
                @cancel="onClickOutsideHandler"
                @done="($event) => handleInteractionSubmit($event)"
                @update-values="handleUpdateValues" />
              <transition v-else name="fade" mode="out-in" duration="300">
                <CreateNewInteraction
                  v-if="show_create_form"
                  :previous-labels="previous_results"
                  :all-labels="all_labels"
                  :result-references-list="result_refs_calc"
                  :result-references-list-for-text="result_refs_name_calc"
                  :autocomplete-options-in-playbook="autocomplete_list_calc"
                  :in-playbook="true"
                  class="pb-16 mt-6"
                  @cancel="onClickOutsideHandler"
                  @done="($event) => handleInteractionSubmit($event)" />
              </transition>
            </div>

            <ChangeMetricActionForm
              v-if="selectedNode?.data.values?.sub_item == 'change_metric'"
              :previous-labels="previous_results"
              :all-labels="all_labels"
              :selected-node="selectedNode"
              @update-values="handleUpdateValues" />

            <ChangeMetricActionForm
              v-if="selectedNode?.data.values?.sub_item?.startsWith('update_')"
              :activity-for-form="selectedNode?.data.values?.sub_item.split('_')[1]"
              :previous-labels="previous_results"
              :all-labels="all_labels"
              :selected-node="selectedNode"
              @update-values="handleUpdateValues" />

            <transition v-else name="fade" mode="out-in" duration="300">
              <SendEmailForm
                v-if="selectedNode?.data.values?.sub_item == 'send_email' && show_create_form"
                :in-playbook="true"
                :validate-on-input="true"
                :selected-node="selectedNode"
                :previous-labels="previous_results"
                :result-references-list="result_refs_calc"
                :result-references-list-for-text="result_refs_name_calc"
                :autocomplete-options-in-playbook="autocomplete_list_calc"
                :all-labels="all_labels"
                @cancel="onClickOutsideHandler"
                @change="
                  ($event) =>
                    handleUpdateValues({
                      ...new_values,
                      ...$event
                    })
                "
                @done="($event) => handleEmailSubmit($event)" />
              <SendSlackForm
                v-else-if="selectedNode?.data.values?.sub_item == 'send_slack' && show_create_form"
                :in-playbook="true"
                :selected-node="selectedNode"
                :previous-results="previous_results"
                :result-references-list="result_refs_calc"
                :result-references-list-for-text="result_refs_name_calc"
                :autocomplete-options-in-playbook="autocomplete_list_calc"
                :all-labels="all_labels"
                @cancel="onClickOutsideHandler"
                @done="($event) => handleSlackSubmit($event)" />
              <NotifyActionForm
                v-else-if="selectedNode?.data.values?.sub_item == 'notify' && show_create_form"
                :selected-node="selectedNode"
                @update-values="handleUpdateValues" />

              <AddToExpansionListActionForm
                v-else-if="selectedNode?.data.values?.sub_item == 'add_to_expansion_list' && show_create_form"
                :selected-node="selectedNode"
                class="pb-16"
                @update-values="handleUpdateValues" />

              <RecordFilterActionForm
                v-else-if="selectedNode?.data.values?.sub_item == 'filter_records'"
                :previous-labels="previous_results"
                :all-labels="all_labels"
                :selected-node="selectedNode"
                @update-values="handleUpdateValues" />

              <ActivityFilterActionForm
                v-else-if="selectedNode?.data.values?.sub_item == 'filter_activities'"
                :previous-labels="previous_results"
                :all-labels="all_labels"
                :selected-node="selectedNode"
                @update-values="handleUpdateValues" />
            </transition>
          </div>

          <div v-if="selectedNode?.data.type == 'wait'">
            <div class="mb-10">
              <TextInput
                :value="action_name"
                name="action_name"
                type="text"
                :validate-on-input="true"
                label=""
                :info-message="$t('Action name must be unique, they can be referenced in the following actions.')"
                :placeholder="$t('Action Name')"
                :show-label="true"
                @update:modelValue="
                  (e) => {
                    action_name = e
                    handleUpdateActionName(e)
                  }
                " />
              <div v-if="all_labels.find((l) => l == action_name)" class="text-red-500">
                {{ $t("Action name already exists") }}
              </div>
            </div>
            <DelayForm :selected-node="selectedNode" @update-values="handleUpdateValues" />
          </div>

          <div v-if="selectedNode?.data.type == 'condition'">
            <div class="mb-10">
              <TextInput
                :value="action_name"
                name="action_name"
                type="text"
                :validate-on-input="true"
                label=""
                :info-message="$t('Action name must be unique, they can be referenced in the following actions.')"
                :placeholder="$t('Action Name')"
                :show-label="true"
                @update:modelValue="
                  (e) => {
                    action_name = e
                    handleUpdateActionName(e)
                  }
                " />
              <div v-if="all_labels.find((l) => l == action_name)" class="text-red-500">
                {{ $t("Action name already exists") }}
              </div>
            </div>
            <SwitchForm
              :previous-labels="previous_results"
              :selected-node="selectedNode"
              :elements="elements"
              @update-values="handleUpdateValues" />
          </div>

          <div v-else-if="selectedNode?.data.type == 'for'">
            <div class="mb-10">
              <TextInput
                :value="action_name"
                name="loop_variable_name"
                type="text"
                :validate-on-input="true"
                label=""
                :placeholder="$t('Loop Variable Name')"
                :show-label="true"
                @update:modelValue="
                  (e) => {
                    action_name = e
                  }
                " />
              <div v-if="all_labels.find((l) => l == action_name)" class="text-red-500">
                {{ $t("Loop name already exists") }}
              </div>
            </div>

            <ForLoopActionForm
              :previous-labels="previous_results"
              :all-labels="all_labels"
              :selected-node="selectedNode"
              @update-values="handleUpdateValues" />
          </div>
        </div>
      </div>
    </template>

    <template v-if="has_save_btn" #footer>
      <div class="flex items-center justify-between w-full">
        <button
          class="text-light-brand-blue px-4 py-2 hover:text-blue-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          @click="onClickOutsideHandler">
          {{ $t("Cancel") }}
        </button>
        <button class="btn primary-btn w-fit px-6 py-2" @click="handleSubmit">
          {{ $t("Save") }}
        </button>
      </div>
    </template>
  </Drawer>
</template>

<script>
import TextInput from "@/components/shared/TextInput.vue"

import SendEmailForm from "@/components/forms/email/SendEmailForm.vue"
import SendSlackForm from "@/components/forms/email/SendSlackForm.vue"

import TriggerForm from "./TriggerForm.vue"

import EditPlan from "@/components/forms/EditPlan.vue"
import CreatePlan from "@/components/forms/CreatePlan.vue"

import UpdateGoalForm from "@/components/forms/UpdateGoalForm.vue"
import NewGoalForm from "@/components/forms/NewGoalForm.vue"

import UpdateTaskForm from "@/components/forms/UpdateTaskForm.vue"
import CreateNewTask from "@/components/forms/CreateNewTask.vue"

import UpdateInteractionForm from "@/components/forms/UpdateInteractionForm.vue"
import CreateNewInteraction from "@/components/forms/CreateNewInteraction.vue"

import ChangeMetricActionForm from "./ChangeMetricActionForm.vue"

import NotifyActionForm from "./NotifyActionForm.vue"
import AddToExpansionListActionForm from "./AddToExpansionListActionForm.vue"

import RecordFilterActionForm from "./RecordFilterActionForm.vue"
import ActivityFilterActionForm from "./ActivityFilterActionForm.vue"

import ForLoopActionForm from "./ForLoopActionForm.vue"

import DelayForm from "./DelayForm.vue"
import SwitchForm from "./SwitchForm.vue"
import { utils } from "@/store/helpers"
import {
  getObjectSchema,
  getLocalSourceDatatype,
  getLocalSourceInputtype
} from "@/views/dashboard/playbooks/PlaybookEditor/forms/schema.js"
import Drawer from "@/components/shared/Drawer.vue"

export default {
  components: {
    Drawer,
    TextInput,
    TriggerForm,
    SendEmailForm,
    SendSlackForm,
    RecordFilterActionForm,
    ActivityFilterActionForm,
    ForLoopActionForm,
    EditPlan,
    CreatePlan,
    UpdateGoalForm,
    NewGoalForm,
    UpdateTaskForm,
    CreateNewTask,
    UpdateInteractionForm,
    CreateNewInteraction,
    ChangeMetricActionForm,
    NotifyActionForm,
    AddToExpansionListActionForm,
    DelayForm,
    SwitchForm
  },
  props: {
    selectedNode: {
      type: Object,
      required: false
    },
    elements: {
      type: Array,
      default: () => []
    },
    getConnectedSourceElements: {
      type: Function,
      required: true
    },
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel", "submit", "update", "update:isOpen", "update-references"],
  data() {
    return {
      utils,
      form_valid: false,
      is_open: false,
      new_values: null,
      plan: null,
      goal: null,
      task: null,
      interaction: null,
      show_create_form: false,
      action_name: null
    }
  },

  computed: {
    has_save_btn() {
      try {
        return (
          this.selectedNode?.data.type == "trigger" ||
          this.selectedNode?.data.values?.sub_item == "change_metric" ||
          this.selectedNode?.data.values?.sub_item == "update_plan" ||
          this.selectedNode?.data.values?.sub_item == "update_goal" ||
          this.selectedNode?.data.values?.sub_item == "update_task" ||
          this.selectedNode?.data.values?.sub_item == "update_interaction" ||
          this.selectedNode?.data.values?.sub_item == "update_milestone" ||
          this.selectedNode?.data.values?.sub_item == "filter_records" ||
          this.selectedNode?.data.values?.sub_item == "filter_activities" ||
          this.selectedNode?.data.values?.sub_item == "notify" ||
          this.selectedNode?.data.values?.sub_item == "add_to_expansion_list" ||
          this.selectedNode?.data.type == "for" ||
          this.selectedNode?.data.type == "wait" ||
          this.selectedNode?.data.type == "condition"
        )
      } catch (_) {
        return false
      }
    },
    all_previous_results() {
      const all_labels_calc = []

      let branches_ended = 1

      let previous_connected_nodes = this.getConnectedSourceElements(this.selectedNode?.id)

      if (previous_connected_nodes.length > 1) {
        branches_ended += previous_connected_nodes.length - 1
      }

      let while_limit = 50
      let while_iterations = 0

      if (previous_connected_nodes.length) {
        while (branches_ended && while_iterations < while_limit) {
          while_iterations += 1

          previous_connected_nodes.forEach((node) => {
            let all_previous_nodes_calc = []

            let previous_connected_nodes_calc = this.getConnectedSourceElements(node.id)

            if (!previous_connected_nodes_calc.length) {
              branches_ended -= 1
            } else {
              if (!previous_connected_nodes_calc.length > 1) {
                branches_ended += previous_connected_nodes_calc.length - 1
              }
              all_previous_nodes_calc = all_previous_nodes_calc.concat(previous_connected_nodes_calc)
            }

            if (node.data?.values?.trigger_name) {
              all_labels_calc.push({
                name: node.data.values.trigger_name,
                type: node.data.values.sub_item,
                object_type: node.data.values.object_type
              })

              all_labels_calc.push({
                name: `${node.data.values.trigger_name} Parent`,
                type: node.data.values.sub_item,
                object_type: node.data.values.object_type
              })
            }
            if (node.data?.values?.action_name) {
              let type = "record"

              if (node.data?.values?.sub_item?.includes("plan")) {
                type = "plan"
              }
              if (node.data?.values?.sub_item?.includes("goal")) {
                type = "goal"
              }
              if (node.data?.values?.sub_item?.includes("task")) {
                type = "task"
              }
              if (node.data?.values?.sub_item?.includes("interaction")) {
                type = "interaction"
              }
              if (node.data?.values?.sub_item?.includes("milestone")) {
                type = "milestone"
              }

              all_labels_calc.push({
                name: node.data.values.action_name,
                type: type,
                object_type: node.data.values.object_type
              })

              all_labels_calc.push({
                name: `${node.data.values.action_name} Parent`,
                type: type,
                object_type: node.data.values.object_type
              })
            }

            previous_connected_nodes = all_previous_nodes_calc
          })
        }
      }

      return all_labels_calc
    },

    previous_results() {
      const all_labels_calc = []

      let branches_ended = 1

      let previous_connected_nodes = this.getConnectedSourceElements(this.selectedNode?.id)

      if (previous_connected_nodes.length > 1) {
        branches_ended += previous_connected_nodes.length - 1
      }

      let while_limit = 50
      let while_iterations = 0
      if (previous_connected_nodes.length) {
        while (branches_ended && while_iterations < while_limit) {
          while_iterations += 1

          previous_connected_nodes.forEach((node) => {
            let all_previous_nodes_calc = []

            let previous_connected_nodes_calc = this.getConnectedSourceElements(node.id)

            if (!previous_connected_nodes_calc.length) {
              branches_ended -= 1
            } else {
              if (!previous_connected_nodes_calc.length > 1) {
                branches_ended += previous_connected_nodes_calc.length - 1
              }
              all_previous_nodes_calc = all_previous_nodes_calc.concat(previous_connected_nodes_calc)
            }

            if (node.data?.values?.trigger_name) {
              let type = "record"

              if (node.data?.values?.sub_item?.includes("plan")) {
                type = "plan"
              }
              if (node.data?.values?.sub_item?.includes("goal")) {
                type = "goal"
              }
              if (node.data?.values?.sub_item?.includes("task")) {
                type = "task"
              }
              if (node.data?.values?.sub_item?.includes("interaction")) {
                type = "interaction"
              }

              if (node.data?.node_in_loop) {
                if (node.data?.node_in_loop == this.selectedNode?.data?.node_in_loop) {
                  all_labels_calc.push({
                    name: node.data.values.trigger_name,
                    type: type,
                    object_type: node.data.values.object_type
                  })

                  all_labels_calc.push({
                    name: `${node.data.values.trigger_name} Parent`,
                    type: type,
                    object_type: node.data.values.object_type
                  })
                }
              } else {
                if (this.selectedNode?.data.type == "for") {
                  if (
                    node.data?.values?.sub_item?.includes("goal") ||
                    node.data?.values?.sub_item?.includes("task") ||
                    node.data?.values?.sub_item?.includes("interaction")
                  ) {
                    all_labels_calc.push({
                      name: `${node.data.values.trigger_name}`,
                      object_to_extract: node.data?.values?.sub_item?.includes("plan") ? "collaborators" : "assigned_to",
                      object_to_extract_label: "Team Members",
                      type: "record",
                      object_type: "contact"
                    })
                  }
                  if (node.data?.values?.sub_item?.includes("plan")) {
                    all_labels_calc.push({
                      name: `${node.data.values.trigger_name}`,
                      object_to_extract: "records",
                      object_to_extract_label: "Records",
                      type: "record",
                      object_type: "company"
                    })
                  }

                  if (
                    !node.data?.values?.sub_item?.includes("plan") &&
                    !node.data?.values?.sub_item?.includes("goal") &&
                    !node.data?.values?.sub_item?.includes("task") &&
                    !node.data?.values?.sub_item?.includes("interaction")
                  ) {
                    if (node.data?.values?.sub_item?.includes("filter")) {
                      all_labels_calc.push({
                        name: node.data.values.trigger_name,
                        type: type,
                        object_type: node.data.values.object_type
                      })

                      all_labels_calc.push({
                        name: `${node.data.values.trigger_name} Parent`,
                        type: type,
                        object_type: node.data.values.object_type
                      })
                    }
                  }
                } else {
                  if (
                    node.data?.values?.sub_item?.includes("goal") ||
                    node.data?.values?.sub_item?.includes("task") ||
                    node.data?.values?.sub_item?.includes("interaction")
                  ) {
                    all_labels_calc.push({
                      name: `${node.data.values.trigger_name}`,
                      object_to_extract: "record",
                      object_to_extract_label: "Record",
                      type: "record",
                      object_type: "company"
                    })
                  }

                  all_labels_calc.push({
                    name: node.data.values.trigger_name,
                    type: type,
                    object_type: node.data.values.object_type
                  })

                  all_labels_calc.push({
                    name: `${node.data.values.trigger_name} Parent`,
                    type: type,
                    object_type: node.data.values.object_type
                  })
                }
              }
            }
            if (node.data?.values?.action_name) {
              if (
                (this.selectedNode?.data.type !== "for" &&
                  node.data.values?.sub_item !== "filter_records" &&
                  node.data.values?.sub_item !== "filter_activities") ||
                (this.selectedNode?.data.type == "for" &&
                  (node.data.values?.sub_item == "filter_records" ||
                    node.data.values?.sub_item == "filter_activities" ||
                    node.data?.values?.sub_item?.includes("plan") ||
                    node.data?.values?.sub_item?.includes("goal") ||
                    node.data?.values?.sub_item?.includes("task") ||
                    node.data?.values?.sub_item?.includes("interaction")))
              ) {
                let type = "record"

                if (node.data?.values?.sub_item?.includes("plan")) {
                  type = "plan"
                }
                if (node.data?.values?.sub_item?.includes("goal")) {
                  type = "goal"
                }
                if (node.data?.values?.sub_item?.includes("task")) {
                  type = "task"
                }
                if (node.data?.values?.sub_item?.includes("interaction")) {
                  type = "interaction"
                }
                if (node.data?.values?.sub_item?.includes("milestone")) {
                  type = "milestone"
                }

                if (node.data.type === "for") {
                  if (this.$utils.slugify(node.data?.values?.action_name) == this.selectedNode?.data?.node_in_loop) {
                    all_labels_calc.push({
                      name: node.data.values.action_name,
                      type: type,
                      object_type:
                        this.all_previous_results.find(
                          (l) => this.$utils.slugify(l.name) == node.data.values.records.replace("${", "").replace("}", "").split(".")[0]
                        )?.object_type ||
                        (node.data.values.records.replace("${", "").replace("}", "").split(".")[1] == "records" ? "company" : "contact")
                    })
                  }
                } else if (node.data?.node_in_loop) {
                  if (node.data?.node_in_loop == this.selectedNode?.data?.node_in_loop) {
                    all_labels_calc.push({
                      name: node.data.values.action_name,
                      type: type,
                      object_type: node.data.values.object_type
                    })

                    all_labels_calc.push({
                      name: `${node.data.values.action_name} Parent`,
                      type: type,
                      object_type: node.data.values.object_type
                    })
                  }
                } else {
                  if (this.selectedNode?.data.type == "for") {
                    if (
                      node.data?.values?.sub_item?.includes("goal") ||
                      node.data?.values?.sub_item?.includes("task") ||
                      node.data?.values?.sub_item?.includes("interaction")
                    ) {
                      all_labels_calc.push({
                        name: `${node.data.values.action_name}`,
                        object_to_extract: node.data?.values?.sub_item?.includes("plan") ? "collaborators" : "assigned_to",
                        object_to_extract_label: "Team Members",
                        type: "record",
                        object_type: "contact"
                      })
                    }

                    if (node.data?.values?.sub_item?.includes("plan")) {
                      all_labels_calc.push({
                        name: `${node.data.values.action_name}`,
                        object_to_extract: "records",
                        object_to_extract_label: "Records",
                        type: "record",
                        object_type: "company"
                      })
                    }

                    if (
                      !node.data?.values?.sub_item?.includes("plan") &&
                      !node.data?.values?.sub_item?.includes("goal") &&
                      !node.data?.values?.sub_item?.includes("task") &&
                      !node.data?.values?.sub_item?.includes("interaction")
                    ) {
                      if (node.data?.values?.sub_item?.includes("filter")) {
                        all_labels_calc.push({
                          name: node.data.values.action_name,
                          type: type,
                          object_type: node.data.values.object_type
                        })
                        all_labels_calc.push({
                          name: `${node.data.values.action_name} Parent`,
                          type: type,
                          object_type: node.data.values.object_type
                        })
                      }
                    }
                  } else {
                    if (
                      node.data?.values?.sub_item?.includes("goal") ||
                      node.data?.values?.sub_item?.includes("task") ||
                      node.data?.values?.sub_item?.includes("interaction")
                    ) {
                      all_labels_calc.push({
                        name: `${node.data.values.action_name}`,
                        object_to_extract: "record",
                        object_to_extract_label: "Record",
                        type: "record",
                        object_type: "company"
                      })
                    }

                    all_labels_calc.push({
                      name: node.data.values.action_name,
                      type: type,
                      object_type: node.data.values.object_type
                    })
                  }
                }
              }
            }

            previous_connected_nodes = all_previous_nodes_calc
          })
        }
      }

      return all_labels_calc
    },

    result_refs_calc() {
      const results = []

      // Helper function to create a consistent reference object
      const createReference = (label, suffix = "", property = "") => {
        const labelName = label.name
        const slugName = this.$utils.slugify(labelName)
        const extractPrefix = label.object_to_extract ? `${label.object_to_extract}.` : ""
        const extractLabel = label.object_to_extract_label || ""

        // Build the property path for the reference
        const propPath = property ? `${extractPrefix}${property}` : property

        // Extract data type and input type from the label or use defaults
        const dataType = label.data_type ? getLocalSourceDatatype(label.data_type) : "String"
        const inputType = label.input_type ? getLocalSourceInputtype(label.input_type) : getLocalSourceInputtype(dataType)

        // Create the reference object
        return {
          label: suffix ? `${labelName} ${extractLabel} ${suffix}`.trim() : `${labelName} ${extractLabel}`.trim(),
          value: `$\{${slugName}.${propPath}}`,
          id: `$\{${slugName}.${propPath}}`,
          data_type: dataType,
          input_type: inputType,
          otype: label.object_type || label.type
        }
      }

      this.previous_results.forEach((label) => {
        // Handle plan type labels
        if (label.type === "plan") {
          results.push(createReference(label, "Records", "companies"))
        }

        // Handle company or opportunity type labels
        if (label.object_type === "company" || label.object_type === "opportunity") {
          results.push(createReference(label, "", "id"))
          results.push(createReference(label, "Parent", "parent"))
        }
      })

      return results
    },

    result_refs_name_calc() {
      const results = []

      // Reusing the helper function created in result_refs_calc method
      const createReference = (label, suffix = "", property = "", additionalProps = {}) => {
        const labelName = label.name
        const slugName = this.$utils.slugify(labelName)
        const extractPrefix = label.object_to_extract ? `${label.object_to_extract}.` : ""
        const extractLabel = label.object_to_extract_label || ""

        // Build the property path for the reference
        const propPath = property ? `${extractPrefix}${property}` : property

        // Extract data type and input type from the label or use defaults
        const dataType = label.data_type ? getLocalSourceDatatype(label.data_type) : "String"
        const inputType = label.input_type ? getLocalSourceInputtype(label.input_type) : getLocalSourceInputtype(dataType)

        // Create the reference object with possible additional properties
        return {
          label: suffix ? `${labelName} ${extractLabel} ${suffix}`.trim() : `${labelName} ${extractLabel}`.trim(),
          value: `$\{${slugName}.${propPath}}`,
          id: `$\{${slugName}.${propPath}}`,
          data_type: dataType,
          input_type: inputType,
          otype: label.object_type || label.type,
          ...additionalProps
        }
      }

      this.previous_results.forEach((label) => {
        // Add Name reference for all labels
        results.push(createReference(label, "Name", "name"))

        // Add Link reference for all labels
        results.push(createReference(label, "Link", "link"))

        // Process metrics for record type with ObjectType metrics
        if (label.type === "record" && label.object_type && utils.ObjectType(label.object_type)?.metrics) {
          utils
            .ObjectType(label.object_type)
            .metrics.filter((metric) => metric.data_type !== "List" && metric.data_type !== "Json")
            .forEach((metric) => {
              results.push({
                label: `${label.name} > ${metric.name}`,
                value: `$\{${this.$utils.slugify(label.name)}.metrics.${metric.reference_name}}`,
                id: `$\{${this.$utils.slugify(label.name)}.metrics.${metric.reference_name}}`,
                data_type: metric.data_type,
                input_type: metric.data_type,
                otype: label.object_type
              })
            })
        } else {
          // Add common fields for activity objects
          const activityFields = getObjectSchema(label.type).properties

          Object.entries(activityFields).forEach(([field, fieldType]) => {
            if (fieldType.type !== "array") {
              results.push({
                label: `${label.name} ${field.title()}`,
                value: `$\{${this.$utils.slugify(label.name)}.${field}}`,
                id: `$\{${this.$utils.slugify(label.name)}.${field}}`,
                data_type: fieldType.type,
                input_type: fieldType.type,
                otype: label.type
              })
            }
          })
        }
      })

      return results
    },

    autocomplete_list_calc() {
      const results = []

      // Helper function to create reference objects with consistent format
      const createReference = (label, suffix = "", property = "", additionalProps = {}) => {
        const labelName = label.name
        const slugName = this.$utils.slugify(labelName)
        const extractPrefix = label.object_to_extract ? `${label.object_to_extract}.` : ""
        const extractLabel = label.object_to_extract_label || ""

        // Build the property path
        const propPath = property ? `${extractPrefix}${property}` : property

        // Extract data type and input type from the label or use defaults
        const dataType = label.data_type ? getLocalSourceDatatype(label.data_type) : "String"
        const inputType = label.input_type ? getLocalSourceInputtype(label.input_type) : getLocalSourceInputtype(dataType)

        // Create the reference object with possible additional properties
        return {
          label: suffix ? `${labelName} ${extractLabel} ${suffix}`.trim() : `${labelName} ${extractLabel}`.trim(),
          value: `$\{${slugName}.${propPath}}`,
          id: `$\{${slugName}.${propPath}}`,
          data_type: dataType,
          input_type: inputType,
          otype: label.object_type || label.type,
          ...additionalProps
        }
      }

      // Process each label in the collection
      this.previous_results.forEach((label) => {
        // Handle contact object type
        if (label.object_type === "contact") {
          results.push(createReference(label, "", "id"))
          results.push(createReference(label, "Parent", "parent"))
        }

        // Handle non-activity objects
        const activityTypes = ["goal", "task", "interaction", "plan"]
        if (!activityTypes.includes(label.type)) {
          // Handle contacts in a loop
          if (label.object_to_extract === "contacts") {
            results.push(createReference(label, "", "id"))
          }

          // Basic ID reference
          results.push(createReference(label, "", "id"))

          // Various owner references
          results.push(createReference(label, "Owner", "owner.id"))
          results.push(createReference(label, "Parent Owner", "parent.owner.id"))

          // Co-owner references (list type)
          results.push(createReference(label, "Co-owners", "co_owners"))
          results.push(createReference(label, "Parent Co-owners", "parent.co_owners"))

          // Tags for all object types
          results.push(createReference(label, "Tags", "tags"))

          // Company or opportunity specific fields
          if (label.object_type === "company" || label.object_type === "opportunity") {
            results.push(createReference(label, "Contacts", "contacts"))
          }
        } else {
          // Process activity objects
          const activityFields = getObjectSchema(label.type).properties

          Object.entries(activityFields).forEach(([field, fieldType]) => {
            if (fieldType.type !== "array") {
              results.push({
                label: `${label.name} ${field.title()}`,
                value: `$\{${this.$utils.slugify(label.name)}.${field}}`,
                id: `$\{${this.$utils.slugify(label.name)}.${field}}`,
                otype: label.type
              })
            }
          })
        }
      })

      return results
    },

    all_labels() {
      const all_labels_calc = []
      this.elements.forEach((el) => {
        if (el.id !== this.selectedNode?.id) {
          if (el.data?.values?.trigger_name) {
            all_labels_calc.push(el.data.values.trigger_name)
          }

          if (el.data?.values?.action_name) {
            all_labels_calc.push(el.data.values.action_name)
          }
        }
      })
      return all_labels_calc
    }
  },
  watch: {
    isOpen() {
      this.is_open = this.isOpen
      if (this.isOpen) {
        this.generateUniqueActionName()
      }
      this.$emit("update:isOpen", this.is_open)
    },
    action_name() {
      this.new_values = { ...this.new_values, action_name: this.action_name }
    }
  },
  created() {
    // Initialize action_name for existing nodes
    if (this.selectedNode?.data.values?.action_name) {
      this.action_name = this.selectedNode.data.values.action_name
    }

    if (this.selectedNode?.data.type == "action") {
      if (this.selectedNode?.data.values?.sub_item == "create_plan" && this.selectedNode?.data.values.name) {
        this.plan = this.selectedNode?.data.values
      }

      if (this.selectedNode?.data.values?.sub_item == "create_goal" && this.selectedNode?.data.values.name) {
        this.goal = this.selectedNode?.data.values
      }

      if (this.selectedNode?.data.values?.sub_item == "create_interaction" && this.selectedNode?.data.values.type) {
        this.interaction = this.selectedNode?.data.values
      }

      if (this.selectedNode?.data.values?.sub_item == "create_task" && this.selectedNode?.data.values.name) {
        this.task = this.selectedNode?.data.values
      }
    }
    setTimeout(() => {
      this.show_create_form = true
    }, 500)
  },

  methods: {
    onClickOutsideHandler() {
      this.$emit("update:isOpen", false)
    },
    generateUniqueActionName() {
      if (this.selectedNode?.data.values?.action_name) {
        this.action_name = this.selectedNode?.data.values?.action_name
      } else {
        let action_name = ""

        // Handle different node types with proper naming
        if (this.selectedNode?.data?.values?.sub_item) {
          // For action nodes with sub_item
          action_name = `${this.$utils.capitalize(
            this.selectedNode.data.values.sub_item.replaceAll("_", " ")
          )} ${this.$utils.capitalize(this.selectedNode?.data?.type).replaceAll("_", " ")}`
        } else {
          // For nodes without sub_item (wait, condition, for, etc.)
          action_name = this.$utils.capitalize(this.selectedNode?.data?.type).replaceAll("_", " ")
        }
        while (this.all_previous_results.map((l) => l.name).includes(action_name)) {
          let number_added = 2
          if (!isNaN(parseInt(action_name.split(" ")[action_name.split(" ").length - 1]))) {
            number_added = parseInt(action_name.split(" ")[action_name.split(" ").length - 1]) + 1
            action_name = action_name
              .split(" ")
              .slice(0, action_name.split(" ").length - 1)
              .join(" ")
          }
          action_name += ` ${number_added}`
        }
        this.action_name = action_name
      }
    },

    handleUpdateValues(values) {
      this.form_valid = values.valid
      this.new_values = { ...values, action_name: this.action_name }
    },
    handleSubmit() {
      delete this.new_values.valid

      if (this.form_valid) {
        this.$emit("submit", this.new_values)
      }
    },
    handleUpdateActionName(name) {
      // Get the old result key for reference updates
      const oldResult = this.selectedNode?.data.values?.result

      // Generate new result key from the new action name
      const newResult = this.$utils.slugify(name)

      // Update the current node's values
      this.new_values = {
        ...this.selectedNode?.data.values,
        action_name: name,
        result: newResult
      }

      // If the result key has changed, emit event to update references in other nodes
      if (oldResult && oldResult !== newResult) {
        this.$emit("update-references", {
          oldReference: oldResult,
          newReference: newResult,
          nodeId: this.selectedNode?.id
        })
      }
    },
    handlePlanSubmit(plan) {
      this.$emit("submit", {
        ...this.selectedNode?.data.values,
        sub_item: "create_plan",
        action_name: this.action_name,
        ...plan,
        meta: { name: plan.name }
      })
    },

    handleGoalSubmit(goal) {
      this.$emit("submit", {
        ...this.selectedNode?.data.values,
        sub_item: "create_goal",
        action_name: this.action_name,
        ...goal,
        meta: { name: goal.name }
      })
    },

    handleTaskSubmit(task) {
      this.$emit("submit", {
        ...this.selectedNode?.data.values,
        action_name: this.action_name,
        ...task,
        meta: { name: task.name }
      })
    },

    handleInteractionSubmit(interaction) {
      this.$emit("submit", {
        ...this.selectedNode?.data.values,
        action_name: this.action_name,
        ...interaction,
        meta: {
          name: `${interaction.type} ${interaction.category ? ` - ${interaction.category}` : ""}`
        }
      })
    },

    handleEmailSubmit(email_data) {
      this.$emit("submit", {
        ...this.selectedNode?.data.values,
        action_name: this.action_name,
        ...email_data,
        meta: { name: `${email_data.subject}` }
      })
    },

    handleSlackSubmit(slack_data) {
      let users_list_calc = []
      const slack_obj = this.$store.state.integration.integrations.find((i) => i.name == "slack")

      if (slack_obj.users?.length) {
        users_list_calc = slack_obj.users
      }

      if (slack_obj.bot_user_id && !users_list_calc.find((u) => u.profile.first_name == "Revsetter")) {
        users_list_calc.push({
          id: "bot",
          profile: { first_name: "Revsetter" }
        })
      }

      this.$emit("submit", {
        ...this.selectedNode?.data.values,
        action_name: this.action_name,
        ...slack_data,
        meta: {
          name: `Sender: ${
            users_list_calc.find((u) => u.id == slack_data.sender)?.profile
              ? users_list_calc.find((u) => u.id == slack_data.sender)?.profile.first_name
              : slack_data.sender.id
                ? slack_data.sender.id
                : slack_data.sender
          }`
        }
      })
    }
  }
}
</script>
